import { useSupportTicketsStore } from "@shared/stores/supportTickets.ts";
import { useNotifications } from "@shared/module-transformers/useNotifications.ts";
import { useSubscriptionsStore } from "@shared/stores/subscriptions.ts";
import { salesforce } from "@shared/services/salesforce.ts";
import { watch } from "vue";
import { rolesService } from "@shared/services/roles.ts";
import logger from "@shared/utils/logger.ts";
import { isRunningOnTour } from "@shared/utils/tour.ts";
import type { SubscriptionDTO } from "@shared/types/common";

export const useSupportTicketsLoader = () => {
  const subscriptionsStore = useSubscriptionsStore();
  const supportTicketsStore = useSupportTicketsStore();

  const loadSupportTickets = async () => {
    try {
      supportTicketsStore.setIsLoading(true);

      const allSupportTickets = await fetchAndMergeSupportTickets();
      supportTicketsStore.setSupportTickets(allSupportTickets);

      supportTicketsStore.setIsLoading(false);
    } catch (e) {
      logger.error(e);
      supportTicketsStore.setIsLoading(false);
      useNotifications().error("Failed to fetch support tickets");
    }
  };

  const extractSubscriptionNumbersToFetch = (): number[] => {
    const allSubscriptions = subscriptionsStore.subscriptions as
      | SubscriptionDTO[]
      | null;

    if (!allSubscriptions) {
      return [];
    }

    if (isRunningOnTour()) {
      return allSubscriptions.map(
        (subscription: SubscriptionDTO) => subscription.accountNumber,
      );
    }

    return allSubscriptions
      .filter((subscription: SubscriptionDTO) => !subscription.meta.isTrial)
      .filter((subscription: SubscriptionDTO) => {
        const roleForSubscription = rolesService.extractUserRoleForSubscription(
          subscription.accountNumber.toString(),
        );
        return roleForSubscription === "MYJFROG_PRIMARY_ADMIN";
      })
      .map((subscription: SubscriptionDTO) => subscription.accountNumber);
  };

  const fetchAndMergeSupportTickets = async () => {
    const subscriptionNumbersToFetch = extractSubscriptionNumbersToFetch();
    const responses = await Promise.all(
      subscriptionNumbersToFetch.map((subscriptionNumber) =>
        salesforce.getSupportTickets(subscriptionNumber),
      ),
    );

    return responses.flat();
  };

  watch(
    () => subscriptionsStore.currentSubscription,
    (currentSubscription) => {
      if (!currentSubscription || !supportTicketsStore.isLoading) {
        return;
      }
      loadSupportTickets();
    },
    {
      immediate: true,
    },
  );
  return {};
};
